import BaseModel from './BaseModel';
import { db } from '../utilities';
import { collection, doc, query, where, getDocs, addDoc, updateDoc, setDoc, documentId } from 'firebase/firestore';

interface Rating {
  id?: string;
  name: string;
  description: string;
  type: string;
  formId: string;
}

class Ratings extends BaseModel {
  constructor() {
    super(collection(db, 'ratings'));
  }

  async getAllRatings(formId: string | undefined): Promise<Rating[]> {
    try {
      if (!formId) {
        console.log("formId is undefined. Skipping query.");
        return [];
      }

      const snapshot = await getDocs(
        query(this.baseQuery, where('status', '==', true), where('formId', '==', formId))
      );

      if (snapshot.empty) {
        return [];
      }

      const ratings = snapshot.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          name: data.name || "",
          description: data.description || "",
          type: data.type || "",
          formId: data.formId || formId,
          order: data.order !== undefined ? data.order : Number.MAX_SAFE_INTEGER,
        };
      });

      return ratings.sort((a, b) => a.order - b.order);

    } catch (error) {
      console.error("Error fetching ratings:", error);
      return [];
    }
  }

  async createRating(rating: Rating): Promise<void> {
    try {
      const snapshot = await getDocs(
        query(
          this.baseQuery,
          where('type', '==', rating.type),
          where('formId', '==', rating.formId),
          where('name', '==', rating.name)
        )
      );
      if (!snapshot.empty) {
        throw new Error(`${rating.type} name must be unique`);
      }
      await addDoc(this.baseQuery, rating);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async updateRating(id: string, rating: Rating): Promise<void> {
    try {
      const snapshot = await getDocs(
        query(
          this.baseQuery,
          where('type', '==', rating.type),
          where('name', '==', rating.name),
          where('formId', '==', rating.formId),
          where(documentId(), '!=', id)
        )
      );
      if (!snapshot.empty) {
        throw new Error(`${rating.type} name must be unique`);
      }
      await setDoc(doc(this.baseQuery, id), rating, { merge: true });
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async deleteRating(id: string): Promise<void> {
    try {
      await updateDoc(doc(this.baseQuery, id), { status: false });
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  async updateRatingOrder(id: string, order: number): Promise<void> {
    try {
      if (!id || order === undefined) {
        throw new Error("ID and order are required.");
      }
      await updateDoc(doc(this.baseQuery, id), { order: order });
    } catch (error) {
      console.error("Error in updateRatingOrder:", error);
      throw error;
    }
  }
}

export default Ratings;
